import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  IconButton,
  Paper,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Table,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";

import { getRuta, simpleMessage } from "../../helpers/Helpers";
import { tienePermiso } from "../../helpers/permisos";
import { formatearMonto } from "../../helpers/formato";
import { deleteToken, deleteUserData, getToken } from "../../api/Account";
import { DataContext } from "../../context/DataContext";
import {
  CargandoTabla,
  ConfirmDialog,
  EstadoChip,
  EstadoError,
  EstadoVacio,
  PageHeader,
  useAviso,
} from "../../components/base";
import {
  deleteCreditAsync,
  desembolsarCreditAsync,
  getCreditsEntregadosCancelled,
  getCreditsEntregadosUncancelled,
  getCreditsNoEntregados,
} from "../../api/Credits";
import AddAbono from "./creditDetails/quoteComponents/AddAbono";
import {
  celdaFecha,
  celdaMonto,
  encabezadoTabla,
  estadoDeCredito,
  fechaDeCredito,
} from "./comunes";

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = "Sesión activa en otro dispositivo";

const ESTADOS = [
  {
    etiqueta: "Entregados",
    vacio: "No hay créditos entregados",
    descripcionVacio: "Aquí aparecen los créditos ya desembolsados que siguen vigentes.",
  },
  {
    etiqueta: "Pendientes de entrega",
    vacio: "No hay créditos pendientes de entrega",
    descripcionVacio: "Aquí aparecen los créditos aprobados que todavía no se desembolsan.",
  },
  {
    etiqueta: "Cancelados",
    vacio: "No hay créditos cancelados",
    descripcionVacio: "Aquí aparecen los créditos que ya se pagaron por completo.",
  },
];

const FILAS_POR_PAGINA = 10;

// Sin tildes y sin mayúsculas, para que "Peña" se encuentre escribiendo "pena".
const normalizar = (texto) =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const obtenerLista = (estado) => {
  if (estado === 0) return getCreditsEntregadosUncancelled;
  if (estado === 1) return getCreditsNoEntregados;
  return getCreditsEntregadosCancelled;
};

const CreditLIst = () => {
  const [creditList, setCreditList] = useState([]);
  const [localReload, setLocalReload] = useState(false);
  const [active, setActive] = useState(0);

  // Estado local de la lista: esqueleto mientras carga y error con "Reintentar".
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const peticionActual = useRef(0);

  const { setIsLoading, setIsLogged, setIsUnautorized, access } =
    useContext(DataContext);
  const aviso = useAviso();
  let ruta = getRuta();
  const token = getToken();
  let navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [pagina, setPagina] = useState(0);
  const [addQuoteModal, setAddQuoteModal] = useState(false);
  const [id, setId] = useState("");
  const [isNominal, setIsNominal] = useState(false);
  const [clienteAbono, setClienteAbono] = useState("");

  // Menú de tres puntos de una fila.
  const [menu, setMenu] = useState({ ancla: null, fila: null });

  // Confirmación de desembolsar y de eliminar. Se conservan `tipo` y `fila` al cerrar
  // para que el texto no cambie durante la animación de salida.
  const [confirmacion, setConfirmacion] = useState({
    abierto: false,
    tipo: null,
    fila: null,
  });
  const [procesando, setProcesando] = useState(false);

  const termino = normalizar(searchTerm.trim());
  const withSearch = creditList.filter((val) => {
    if (termino === "") return true;
    return (
      normalizar(val.id).includes(termino) ||
      normalizar(val.client?.fullName).includes(termino) ||
      normalizar(val.client?.identification).includes(termino)
    );
  });

  const cargar = async (estado) => {
    const peticion = ++peticionActual.current;
    setCargando(true);
    setError(false);
    setIsLoading(true);
    try {
      const result = await obtenerLista(estado)(token);
      // Si mientras tanto se cambió de estado, esta respuesta ya no importa.
      if (peticion !== peticionActual.current) return;

      if (!result.statusResponse) {
        setIsLoading(false);
        setCargando(false);
        if (result.error === "eX01") {
          deleteUserData();
          deleteToken();
          setIsLogged(false);
          return;
        }
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        setError(true);
        if (result.error === ERROR_OTRA_SESION) {
          simpleMessage(result.error, "error");
        }
        return;
      }

      setIsLoading(false);
      setCreditList(result.data.result);
      setCargando(false);
    } catch (e) {
      // Sin red, `errorResponse` lanza una excepción: ni esqueleto eterno ni overlay encendido.
      if (peticion !== peticionActual.current) return;
      setIsLoading(false);
      setCargando(false);
      setError("conexion");
    }
  };

  useEffect(() => {
    cargar(active);
  }, [localReload]);

  // Si al buscar, cambiar de estado o eliminar la página actual queda fuera de rango,
  // se vuelve a la primera: así no aparece un "No hay datos" falso.
  useEffect(() => {
    if (pagina > 0 && pagina * FILAS_POR_PAGINA >= withSearch.length) {
      setPagina(0);
    }
  }, [pagina, withSearch.length]);

  const currentItem = withSearch.slice(
    pagina * FILAS_POR_PAGINA,
    (pagina + 1) * FILAS_POR_PAGINA
  );

  const onChangeSearch = (val) => {
    setPagina(0);
    setSearchTerm(val);
  };

  const handleChange = (event, nuevoEstado) => {
    if (nuevoEstado === active) return;
    setActive(nuevoEstado);
    setPagina(0);
    cargar(nuevoEstado);
  };

  const deleteCredit = async (item) => {
    setIsLoading(true);
    try {
      const result = await deleteCreditAsync(token, item.id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }
      setIsLoading(false);
      setLocalReload(!localReload);
      aviso.exito(`Crédito #${item.id} eliminado`);
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const desembolsarCredito = async (id) => {
    setIsLoading(true);

    try {
      const result = await desembolsarCreditAsync(token, id);
      if (!result.statusResponse) {
        setIsLoading(false);
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }
      setIsLoading(false);
      setActive(0);
      setLocalReload(!localReload);
      navigate(`${ruta}/credit/${id}`);
    } catch (e) {
      setIsLoading(false);
      aviso.error("No se pudo conectar con el servidor. Inténtalo de nuevo.");
    }
  };

  const pedirConfirmacion = (tipo, fila) => {
    setConfirmacion({ abierto: true, tipo, fila });
  };

  const cerrarConfirmacion = () => {
    setConfirmacion((actual) => ({ ...actual, abierto: false }));
  };

  // Solo aquí se llama a la función de verdad, y una sola vez por confirmación.
  const confirmar = async () => {
    const { tipo, fila } = confirmacion;
    setProcesando(true);
    try {
      if (tipo === "desembolsar") await desembolsarCredito(fila.id);
      else await deleteCredit(fila);
    } finally {
      setProcesando(false);
      cerrarConfirmacion();
    }
  };

  const abrirAbono = (row) => {
    setId(row.id);
    setIsNominal(row.isNominal);
    setClienteAbono(row.client?.fullName ?? "");
    setAddQuoteModal(true);
  };

  const verCredito = (row) => {
    navigate(`${ruta}/credit/${row.id}`);
  };

  // Cada acción pide el permiso que exige su endpoint (CreditsController): sin él, el servidor
  // responde 401 y la web cierra la sesión.
  const puedeAbonar = tienePermiso(access, "CREDITS CREATE"); // AddQuote y AddQuoteNominal
  const puedeDesembolsar = tienePermiso(access, "CREDITS UPDATE"); // DesembolsarCredit
  const puedeVer = tienePermiso(access, "CREDITS VER"); // GetCreditById
  const puedeEliminar = tienePermiso(access, "CREDITS DELETE"); // DeleteCredit
  const puedeAgregar = tienePermiso(access, "CREDITS CREATE"); // AddCredit

  // Acciones de una fila, de la más usada a la menos usada.
  const accionesDeFila = (row) => {
    const lista = [];
    if (active === 0 && puedeAbonar) {
      lista.push({
        clave: "abonar",
        texto: "Abonar",
        icono: <PaymentsOutlinedIcon />,
        color: "success.main",
        alHacerClic: () => abrirAbono(row),
      });
    } else if (active === 1 && puedeDesembolsar) {
      lista.push({
        clave: "desembolsar",
        texto: "Desembolsar",
        icono: <RequestQuoteOutlinedIcon />,
        color: "primary.main",
        alHacerClic: () => pedirConfirmacion("desembolsar", row),
      });
    }
    if (puedeVer) {
      lista.push({
        clave: "ver",
        texto: "Ver crédito",
        icono: <VisibilityOutlinedIcon />,
        color: "primary.main",
        alHacerClic: () => verCredito(row),
      });
    }
    if (puedeEliminar) {
      lista.push({
        clave: "eliminar",
        texto: "Eliminar",
        icono: <DeleteOutlineIcon />,
        color: "error.main",
        alHacerClic: () => pedirConfirmacion("eliminar", row),
      });
    }
    return lista;
  };

  const botonDeAccion = (accion) => (
    <Tooltip key={accion.clave} title={accion.texto}>
      <IconButton
        aria-label={accion.texto}
        onClick={accion.alHacerClic}
        sx={{ color: accion.color }}
      >
        {accion.icono}
      </IconButton>
    </Tooltip>
  );

  const mostrarAcciones =
    (active === 0 && puedeAbonar) ||
    (active === 1 && puedeDesembolsar) ||
    puedeVer ||
    puedeEliminar;

  const hayBusqueda = termino !== "";
  const estadoActual = ESTADOS[active];

  const botonAgregar = (
    <Button
      onClick={() => {
        navigate(`${ruta}/credit/add/0`);
      }}
      startIcon={<AddIcon />}
      variant="contained"
    >
      Agregar crédito
    </Button>
  );

  const esConfirmacionDeEliminar = confirmacion.tipo === "eliminar";
  const filaConfirmacion = confirmacion.fila;

  let contenido;
  if (cargando) {
    contenido = <CargandoTabla filas={8} columnas={6} etiqueta="Cargando créditos…" />;
  } else if (error) {
    contenido = (
      <EstadoError
        titulo={
          error === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudieron cargar los créditos"
        }
        onReintentar={() => cargar(active)}
      />
    );
  } else if (withSearch.length === 0) {
    contenido = hayBusqueda ? (
      <EstadoVacio
        titulo="Sin resultados"
        descripcion={`Ningún crédito de «${estadoActual.etiqueta}» coincide con «${searchTerm.trim()}». Revisa el número, el nombre o la cédula.`}
        accion={
          <Button variant="outlined" onClick={() => onChangeSearch("")}>
            Borrar búsqueda
          </Button>
        }
      />
    ) : (
      <EstadoVacio
        titulo={estadoActual.vacio}
        descripcion={estadoActual.descripcionVacio}
        accion={puedeAgregar && active !== 2 ? botonAgregar : undefined}
      />
    );
  } else {
    contenido = (
      <>
        <TableContainer>
          <Table aria-label="Lista de créditos">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  #
                </TableCell>
                <TableCell sx={encabezadoTabla}>Cliente</TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Estado
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    textAlign: "right",
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Monto entregado
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    textAlign: "right",
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Cuota
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    display: { xs: "none", md: "table-cell" },
                  }}
                >
                  Entrega
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Vencimiento
                </TableCell>
                <TableCell
                  sx={{
                    ...encabezadoTabla,
                    display: { xs: "none", md: "table-cell" },
                  }}
                >
                  Nominal
                </TableCell>
                {mostrarAcciones ? (
                  <TableCell sx={{ ...encabezadoTabla, textAlign: "right" }}>
                    Acciones
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentItem.map((row) => {
                const {
                  id,
                  client,
                  montoEntregado,
                  fechaEntrega,
                  fechaVencimiento,
                  cuotaPagar,
                  isNominal,
                } = row;
                const acciones = mostrarAcciones ? accionesDeFila(row) : [];
                const visibles = acciones.length > 2 ? acciones.slice(0, 2) : acciones;
                const enMenu = acciones.length > 2 ? acciones.slice(2) : [];

                return (
                  <TableRow key={id} hover>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        fontVariantNumeric: "tabular-nums",
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {id}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {client?.fullName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", whiteSpace: "nowrap" }}
                      >
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ display: { xs: "block", sm: "none" } }}
                        >
                          {`#${id}`}
                        </Typography>
                        {client?.identification}
                      </Typography>
                      <Typography
                        variant="monto"
                        component="span"
                        sx={{ display: { xs: "block", sm: "none" }, fontSize: "0.875rem" }}
                      >
                        {formatearMonto(montoEntregado)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      <EstadoChip {...estadoDeCredito(row)} />
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaMonto, display: { xs: "none", sm: "table-cell" } }}
                    >
                      {formatearMonto(montoEntregado)}
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaMonto, display: { xs: "none", sm: "table-cell" } }}
                    >
                      {formatearMonto(cuotaPagar)}
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaFecha, display: { xs: "none", md: "table-cell" } }}
                    >
                      {fechaDeCredito(fechaEntrega)}
                    </TableCell>
                    <TableCell
                      sx={{ ...celdaFecha, display: { xs: "none", sm: "table-cell" } }}
                    >
                      {fechaDeCredito(fechaVencimiento)}
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      {isNominal ? "Sí" : "No"}
                    </TableCell>
                    {mostrarAcciones ? (
                      <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {visibles.map(botonDeAccion)}
                        {enMenu.length > 0 ? (
                          <Tooltip title="Más acciones">
                            <IconButton
                              aria-label={`Más acciones del crédito #${id}`}
                              aria-haspopup="menu"
                              onClick={(e) =>
                                setMenu({ ancla: e.currentTarget, fila: row })
                              }
                              sx={{ color: "text.secondary" }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={withSearch.length}
          page={pagina}
          onPageChange={(e, nueva) => setPagina(nueva)}
          rowsPerPage={FILAS_POR_PAGINA}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(tipo) =>
            ({
              first: "Primera página",
              last: "Última página",
              next: "Página siguiente",
              previous: "Página anterior",
            }[tipo])
          }
        />
      </>
    );
  }

  // Las acciones del menú de la fila abierta (las que no caben a la vista).
  const accionesDelMenu = menu.fila ? accionesDeFila(menu.fila).slice(2) : [];

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Créditos"
        subtitulo="Consulta, abona y desembolsa los créditos de la cartera"
        icono={<FontAwesomeIcon icon={faCreditCard} />}
        acciones={puedeAgregar ? botonAgregar : undefined}
      />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Tabs
            value={active}
            onChange={handleChange}
            aria-label="Estado del crédito"
            variant="scrollable"
            scrollButtons="auto"
          >
            {ESTADOS.map((estado, indice) => (
              <Tab
                key={estado.etiqueta}
                label={estado.etiqueta}
                id={`creditos-tab-${indice}`}
                aria-controls="creditos-panel"
              />
            ))}
          </Tabs>

          <TextField
            size="small"
            type="search"
            onChange={(e) => onChangeSearch(e.target.value)}
            value={searchTerm}
            placeholder="Número, cliente o cédula"
            inputProps={{ "aria-label": "Buscar crédito" }}
            sx={{ width: { xs: "100%", md: 380 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon aria-hidden="true" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Borrar búsqueda"
                    onClick={() => onChangeSearch("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Stack>

        <div
          role="tabpanel"
          id="creditos-panel"
          aria-labelledby={`creditos-tab-${active}`}
        >
          {contenido}
        </div>
      </Paper>

      <Menu
        anchorEl={menu.ancla}
        open={Boolean(menu.ancla)}
        onClose={() => setMenu((actual) => ({ ...actual, ancla: null }))}
      >
        {accionesDelMenu.map((accion) => (
          <MenuItem
            key={accion.clave}
            onClick={() => {
              setMenu((actual) => ({ ...actual, ancla: null }));
              accion.alHacerClic();
            }}
            sx={{ color: accion.clave === "eliminar" ? "error.main" : undefined }}
          >
            <ListItemIcon sx={{ color: accion.color }}>{accion.icono}</ListItemIcon>
            <ListItemText>{accion.texto}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <ConfirmDialog
        abierto={confirmacion.abierto}
        peligro={esConfirmacionDeEliminar}
        cargando={procesando}
        titulo={esConfirmacionDeEliminar ? "Eliminar crédito" : "Desembolsar crédito"}
        mensaje={
          filaConfirmacion ? (
            esConfirmacionDeEliminar ? (
              <>
                <Typography component="p" sx={{ mb: 1 }}>
                  {`Se eliminará el crédito #${filaConfirmacion.id} de ${filaConfirmacion.client?.fullName} por ${formatearMonto(filaConfirmacion.montoEntregado)}.`}
                </Typography>
                <Typography component="p" sx={{ color: "text.secondary" }}>
                  Esta acción no se puede deshacer.
                </Typography>
              </>
            ) : (
              <>
                <Typography component="p" sx={{ mb: 1 }}>
                  {`Se desembolsará el crédito #${filaConfirmacion.id} de ${filaConfirmacion.client?.fullName} por ${formatearMonto(filaConfirmacion.montoEntregado)}.`}
                </Typography>
                <Typography component="p" sx={{ color: "text.secondary" }}>
                  Pasará a la lista de entregados y se abrirá su detalle.
                </Typography>
              </>
            )
          ) : null
        }
        textoConfirmar={esConfirmacionDeEliminar ? "Eliminar" : "Desembolsar"}
        onConfirmar={confirmar}
        onCancelar={cerrarConfirmacion}
      />

      <AddAbono
        abierto={addQuoteModal}
        cliente={clienteAbono}
        setShowModal={setAddQuoteModal}
        id={id}
        localReload={localReload}
        setLocalReload={setLocalReload}
        isNominal={isNominal}
      />
    </Container>
  );
};

export default CreditLIst;
